import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, KanbanSquare, Users, BookOpen, LogOut, Briefcase, Presentation, Award, PlayCircle, ShieldCheck, CalendarDays, Megaphone, FileCheck2, BadgeEuro, UserRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { RoleBadge } from "@/components/Badges";
import { CommandPalette } from "@/components/CommandPalette";
import { NotificheBell } from "@/components/layout/NotificheBell";
import { KudosBadge } from "@/components/layout/KudosBadge";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/context/ThemeContext";

const NAV = [
  { to: "/dashboard", label: "Pratiche", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/dashboard?ordine=prontezza", label: "Documenti da finalizzare", icon: FileCheck2, id: "nav-documenti" },
  { to: "/bandi", label: "Scadenze", icon: CalendarDays, id: "nav-bandi" },
  { to: "/tasks", label: "Bacheca task", icon: KanbanSquare, id: "nav-tasks" },
  { to: "/team", label: "Team", icon: Users, id: "nav-team" },
  { to: "/gestione", label: "Gestione commerciale", icon: BadgeEuro, id: "nav-gestione", admin: true },
  { sep: "Strumenti" },
  { to: "/report-360", label: "Report Impresa 360", icon: ShieldCheck, id: "nav-report-360" },
  { to: "/aree", label: "Aree professionali", icon: Briefcase, id: "nav-aree" },
  { to: "/bando", label: "Riferimenti bando", icon: BookOpen, id: "nav-bando" },
  { to: "/social", label: "Social", icon: Megaphone, id: "nav-social" },
  { to: "/presentazione", label: "Presentazione team", icon: Presentation, id: "nav-presentazione" },
  { to: "/caso-studio", label: "Caso di studio", icon: Award, id: "nav-caso-studio" },
  { to: "/guide-video", label: "Guide video", icon: PlayCircle, id: "nav-guide-video" },
  { to: "/profilo", label: "Il mio profilo pubblico", icon: UserRound, id: "nav-profilo" },
];

export function AppShell({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-slate-200 bg-slate-100/70 fixed inset-y-0">
        <div className="px-3 pt-4 pb-3 border-b border-slate-200">
          <div className="rounded-xl bg-white border border-slate-200 shadow-sm px-3 py-2.5 flex items-center gap-3" data-testid="sidebar-logo-box">
            <img src="/brand/beaichy-logo.svg" alt="BeAIchy" className="h-9 w-auto" data-testid="brand-logo" />
            <div className="h-8 w-px bg-slate-200" />
            <div className="flex flex-col items-center gap-0.5"><a href="https://www.casaenergiagreen.net" target="_blank" rel="noreferrer" title="Casa Energia Green" className="inline-flex hover:opacity-80"><img src="/brand/casa-energia-green.svg" alt="Casa Energia Green" className="h-7 w-auto" data-testid="partner-logo-ceg" /></a><a href="https://faberpro.app" target="_blank" rel="noreferrer" title="FaberPro" className="inline-flex hover:opacity-80"><img src="/brand/faberpro-logo.png" alt="FaberPro" className="h-4 w-auto" data-testid="sidebar-faberpro-logo" /></a></div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {NAV.filter((n) => n.sep || !n.admin || ["admin", "project_manager"].includes(user?.role)).map((n) => n.sep ? <div key={n.sep} className="pt-4 pb-1 px-3 text-[10px] uppercase tracking-[0.15em] text-slate-400" data-testid="nav-sep-strumenti">{n.sep}</div> : (
            <NavLink key={n.to} to={n.to} end={n.to.startsWith("/dashboard")} data-testid={n.id}
              className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${isActive && (!n.to.includes("?") ? !window.location.search.includes("ordine=prontezza") : window.location.search.includes("ordine=prontezza")) ? "bg-white text-[#003087] shadow-sm border border-slate-200" : "text-slate-600 hover:bg-white/70 hover:text-slate-900"}`}>
              <n.icon className="h-4.5 w-4.5" strokeWidth={1.5} size={18} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="mb-3 px-1"><div className="text-[10px] uppercase tracking-[0.12em] text-slate-400 mb-1.5">In collaborazione con</div><div className="flex items-center gap-3"><a href="https://faberpro.app" target="_blank" rel="noreferrer" title="FaberPro" className="inline-flex hover:opacity-80"><img src="/brand/faberpro-logo.png" alt="FaberPro" className="h-6 w-auto" data-testid="partner-logo" /></a></div></div>
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-[#003087] text-white flex items-center justify-center text-sm font-semibold overflow-hidden">
              {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-900 truncate" data-testid="current-user-name">{user?.name}</div>
              <div className="flex items-center gap-1.5"><RoleBadge role={user?.role} testId="current-user-role" /><KudosBadge userId={user?.id} /></div>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <ThemeToggle full className="flex-1" />
            <Button variant="ghost" size="sm" className="justify-start text-slate-600" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="h-4 w-4 mr-2" /> Esci
            </Button>
          </div>
        </div>
      </aside>

      <div className="flex-1 md:ml-64 min-w-0">
        <header className="sticky top-0 z-20 h-16 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-6 md:px-10">
          <div className="min-w-0">
            <h1 className="font-heading text-xl font-semibold text-slate-900 truncate">{title}</h1>
            {subtitle && <p className="text-xs text-slate-500 truncate">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-3"><CommandPalette />{actions}<NotificheBell /></div>
        </header>
        <main className="px-6 md:px-10 py-8">{children}</main>
      </div>
    </div>
  );
}
